// Reading-order navigation across a course outline. Lessons and sections are
// ordered by their `order` field (not array position), then flattened, so
// "previous/next section" step through the whole course — crossing lesson
// boundaries — rather than jumping by lesson.

/**
 * Flatten an outline into a single ordered list of { lessonId, sectionId },
 * lessons by `order` then sections by `order` within each lesson.
 * @param {{ lessons?: Array<{ lessonId: string, order?: number, sections?: Array<{ sectionId: string, order?: number }> }> }} outline
 * @returns {Array<{ lessonId: string, sectionId: string }>}
 */
export function flattenSections(outline) {
  const lessons = outline?.lessons?.slice().sort((a, b) => (a.order || 0) - (b.order || 0)) || [];
  return lessons.flatMap((l) =>
    (l.sections || [])
      .slice()
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .map((s) => ({ lessonId: l.lessonId, sectionId: s.sectionId }))
  );
}

/**
 * The section immediately before `sectionId` in reading order. Crosses lesson
 * boundaries: the section before a lesson's first section is the PREVIOUS
 * lesson's LAST section (e.g. 5.1 -> 4.N). Returns null for the very first
 * section of the course, or when the section isn't in the outline.
 * @returns {{ lessonId: string, sectionId: string } | null}
 */
export function previousSectionOf(outline, sectionId) {
  const flat = flattenSections(outline);
  const idx = flat.findIndex((x) => x.sectionId === sectionId);
  return idx > 0 ? flat[idx - 1] : null;
}
